import { useTranslation } from "react-i18next";
import { Search, X } from "lucide-react";
import { Input } from "@/shared/components/ui/input";
import { Button } from "@/shared/components/ui/button";
import { PROPERTY_STATUS_FILTERS } from "./constants";
import type { PropertyStatusFilter } from "./types";

interface PropertiesFiltersProps {
  readonly query: string;
  readonly status: PropertyStatusFilter;
  readonly onQueryChange: (value: string) => void;
  readonly onStatusChange: (value: PropertyStatusFilter) => void;
  readonly onReset: () => void;
}

const SELECT_CLASS =
  "h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";

export function PropertiesFilters({
  query,
  status,
  onQueryChange,
  onStatusChange,
  onReset,
}: PropertiesFiltersProps) {
  const { t } = useTranslation();
  const hasFilters = query.length > 0 || status !== "all";

  return (
    <div className="flex flex-wrap items-end gap-3">
      <div className="min-w-[260px] flex-1 space-y-1.5">
        <label
          htmlFor="properties-search"
          className="font-sans text-sm font-medium text-foreground"
        >
          {t("properties.filters.search")}
        </label>
        <div className="relative">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <Input
            id="properties-search"
            data-testid="properties-search-input"
            type="search"
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            placeholder={t("properties.filters.searchPlaceholder")}
            className="pl-9"
            autoComplete="off"
          />
        </div>
      </div>

      <div className="space-y-1.5">
        <label
          htmlFor="properties-status-filter"
          className="font-sans text-sm font-medium text-foreground"
        >
          {t("properties.filters.status")}
        </label>
        <select
          id="properties-status-filter"
          data-testid="filter-status"
          value={status}
          onChange={(e) => onStatusChange(e.target.value as PropertyStatusFilter)}
          className={SELECT_CLASS}
        >
          {PROPERTY_STATUS_FILTERS.map((s) => (
            <option key={s} value={s}>
              {s === "all" ? t("properties.filters.statusAll") : t(`properties.statusLabel.${s}`)}
            </option>
          ))}
        </select>
      </div>

      {hasFilters && (
        <Button
          data-testid="properties-filters-reset"
          variant="ghost"
          onClick={onReset}
          className="h-10"
        >
          <X className="mr-1 h-4 w-4" />
          {t("properties.filters.reset")}
        </Button>
      )}
    </div>
  );
}
